import { useGetQConfigByIdQuery, usePublishQConfigMutation } from '@exam-management/graphql';
import { Navigate } from '@exam-management/router';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Outlet } from 'react-router-dom';

import type { QConfigInformation } from './types';

interface QConfigProviderProps extends PropsWithChildren {
  qconfigId: string;
  courseId: string;
}

// NOTE: Need to use an any because FieldValues can't support z.object
interface FormMethods {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formContext: UseFormReturn<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (data: any) => Promise<void>;
}

interface QConfigPublishError {
  code: string;
  value: Record<string, unknown>[];
}

interface QConfigContextData {
  qConfigData: QConfigInformation;
  courseId: string;
  formMethodsRef: React.RefObject<FormMethods>;
  publishQConfig: {
    execute: ReturnType<typeof usePublishQConfigMutation>[0];
    clearErrors: () => void;
    loading: boolean;
    errors?: QConfigPublishError[];
  };
}

const QConfigContext = createContext<QConfigContextData | undefined>(undefined);

function QConfigProvider(props: Readonly<QConfigProviderProps>) {
  const { qconfigId, courseId, children } = props;
  const [publishErrors, setPublishErrors] = useState<QConfigPublishError[]>([]);

  const { data: getQConfigResult, loading: isLoading } = useGetQConfigByIdQuery({
    variables: {
      getQConfigByIdInput: {
        qConfigId: qconfigId,
      },
    },
  });

  const qConfigData = useMemo(
    () => getQConfigResult?.excGetQConfigById?.qConfig ?? ({} as QConfigInformation),
    [getQConfigResult],
  );

  // NOTE: We need to use ref to pass the form methods to the children components
  const formMethodsRef = useRef<FormMethods>(null);

  const [publishQConfig, { loading: isPublishing, error: publishQConfigErrors }] =
    usePublishQConfigMutation({
      variables: {
        publishQConfigInput: {
          qConfigId: qconfigId,
        },
      },
    });

  const clearPublishErrors = () => {
    setPublishErrors([]);
  };

  const contextValue = useMemo(
    () => ({
      qConfigData,
      courseId,
      formMethodsRef,
      publishQConfig: {
        execute: publishQConfig,
        clearErrors: clearPublishErrors,
        loading: isPublishing,
        errors: publishErrors,
      },
    }),
    [
      qConfigData,
      courseId,
      formMethodsRef,
      publishQConfig,
      clearPublishErrors,
      isPublishing,
      publishErrors,
    ],
  );

  useEffect(() => {
    if (publishQConfigErrors) {
      const graphQLError = publishQConfigErrors?.graphQLErrors[0].extensions?.details;
      const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];
      const errorDetails = extensionDetails
        .map((detail) => ({
          code: detail?.code as string,
          value: detail?.value,
        }))
        .filter((detail) => Boolean(detail.code));
      setPublishErrors(errorDetails);
    }
  }, [publishQConfigErrors]);

  if (isLoading) {
    return <Loading height='100%' />;
  }

  if (!getQConfigResult?.excGetQConfigById) {
    // TODO: Navigate to error page instead
    return <Navigate to='/exam-courses/:courseId/qconfigs' params={{ courseId }} />;
  }

  return (
    <QConfigContext.Provider value={contextValue}>{children ?? <Outlet />}</QConfigContext.Provider>
  );
}
export { QConfigContext, QConfigProvider };
